<template>
  <div>
    <Breadcrumbs title="Package Categories" />

    <div class="container-fluid">
      <div class="row">
        <div class="col-sm-12">
          <div class="card">
            <div class="card-header pb-0">
              <div class="d-flex justify-content-between align-items-center">
                <h4 class="mb-0">
                  <i class="fas fa-tags me-2"></i>
                  Package Categories Management
                </h4>
                <button class="btn btn-primary" @click="openCreateModal" :disabled="!canCreate('packages')">
                  <i class="fas fa-plus me-1"></i>
                  Create Category
                </button>
              </div>
            </div>

            <div class="card-body">
              <!-- Filters and Search -->
              <div class="row mb-4">
                <div class="col-md-4">
                  <div class="input-group">
                    <span class="input-group-text">
                      <i class="fas fa-search"></i>
                    </span>
                    <input
                      v-model="searchQuery"
                      type="text"
                      class="form-control"
                      placeholder="Search categories..."
                      @input="handleSearch"
                    />
                  </div>
                </div>
                <div class="col-md-3">
                  <select
                    v-model="filterStatus"
                    class="form-select"
                    @change="handleFilter"
                  >
                    <option value="">All Status</option>
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>
                <div class="col-md-3">
                  <select
                    v-model="sortBy"
                    class="form-select"
                    @change="handleFilter"
                  >
                    <option value="sortOrder">Sort Order</option>
                    <option value="name">Name</option>
                    <option value="created_at">Created Date</option>
                    <option value="updated_at">Updated Date</option>
                  </select>
                </div>
                <div class="col-md-2">
                  <button
                    class="btn btn-outline-secondary w-100"
                    @click="resetFilters"
                    title="Reset Filters"
                  >
                    <i class="fas fa-undo"></i>
                  </button>
                </div>
              </div>

              <!-- Loading State -->
              <div v-if="loading" class="text-center py-5">
                <div class="spinner-border text-primary" role="status">
                  <span class="visually-hidden">Loading...</span>
                </div>
              </div>

              <!-- Categories Table -->
              <div v-else-if="categories.length > 0" class="table-responsive table_style">
                <table class="table table-striped table-hover align-middle">
                  <thead class="table-dark">
                    <tr>
                      <th scope="col" style="width: 80px">Sr No</th>
                      <th scope="col" style="width: 80px">Color</th>
                      <th scope="col" style="width: 80px">Icon</th>
                      <th scope="col" style="min-width: 200px">Name</th>
                      <th scope="col" style="width: 150px">Key</th>
                      <th scope="col" style="min-width: 200px">Description</th>
                      <th scope="col" style="width: 150px">Sort Order</th>
                      <th scope="col" style="width: 80px">Status</th>
                      <th scope="col" style="width: 120px">Created</th>
                      <th scope="col" style="width: 150px">Actions</th>
                    </tr>
                  </thead>
                  <tbody class="table-group-divider">
                    <tr
                      v-for="(category, index) in categories"
                      :key="category._id"
                    >
                      <td class="text-center">
                        <span class="badge bg-secondary">
                          {{ (currentPage - 1) * itemsPerPage + index + 1 }}
                        </span>
                      </td>
                      <td class="text-center">
                        <div
                          class="color-indicator"
                          :style="{
                            backgroundColor: category.color || '#6c757d',
                          }"
                          :title="category.color"
                        ></div>
                      </td>
                      <td class="text-center">
                        <i
                          :class="`fas fa-${category.icon || 'tag'} text-muted`"
                          style="font-size: 1.2rem"
                          :title="category.icon"
                        ></i>
                      </td>
                      <td>
                        <div>
                          <strong>{{ category.name }}</strong>
                        </div>
                      </td>
                      <td>
                        <code class="bg-light px-2 py-1 rounded">{{
                          category.key
                        }}</code>
                      </td>
                      <td>
                        <span class="text-muted">{{
                          category.description || "No description"
                        }}</span>
                      </td>
                      <td class="text-center">
                        <span class="badge bg-info">{{
                          category.sortOrder || 0
                        }}</span>
                      </td>
                      <td class="text-center">
                        <div class="form-check form-switch">
                          <input
                            class="form-check-input"
                            type="checkbox"
                            :checked="category.isActive"
                            @change="handleToggleStatus(category._id, $event)"
                            :disabled="loading || !canUpdate('packages')"
                          />
                          <label class="form-check-label text-xs">
                            {{ category.isActive ? "Active" : "Inactive" }}
                          </label>
                        </div>
                      </td>
                      <td>
                        <small class="text-muted">
                          {{
                            category.createdAt
                              ? formatDate(category.createdAt)
                              : "N/A"
                          }}
                        </small>
                      </td>
                      <td>
                        <div class="d-flex gap-2" role="group">
                          <button
                            class="btn btn-sm btn-outline-primary"
                            @click="viewCategory(category)"
                            title="View"
                            :disabled="!canView('packages')"
                          >
                            <i class="fas fa-eye"></i>
                          </button>
                          <button
                            class="btn btn-sm btn-outline-success"
                            @click="editCategory(category)"
                            title="Edit"
                            :disabled="!canUpdate('packages')"
                          >
                            <i class="fas fa-edit"></i>
                          </button>
                          <button
                            class="btn btn-sm btn-outline-danger"
                            @click="deleteCategory(category._id)"
                            title="Delete"
                            :disabled="!canDelete('packages')"
                          >
                            <i class="fas fa-trash"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Empty State -->
              <div v-else class="text-center py-5">
                <i class="fas fa-tags fa-3x text-muted mb-3"></i>
                <h5 class="text-muted">No package categories found</h5>
                <p class="text-muted">
                  Create your first package category to get started.
                </p>
                <button class="btn btn-primary" @click="openCreateModal" :disabled="!canCreate('packages')">
                  <i class="fas fa-plus me-1"></i>
                  Create Category
                </button>
              </div>

              <!-- Pagination -->
              <div
                v-if="totalItems > 0"
                class="d-flex justify-content-between align-items-center mt-4 pt-3 border-top"
              >
                <div class="text-muted">
                  <small>
                    Showing {{ startItem }} to {{ endItem }} of
                    {{ totalItems }} categories
                  </small>
                </div>
                <nav v-if="totalPages > 1" aria-label="Categories pagination">
                  <ul class="pagination pagination-sm mb-0">
                    <li
                      class="page-item"
                      :class="{ disabled: currentPage <= 1 }"
                    >
                      <button
                        class="page-link"
                        @click="changePage(currentPage - 1)"
                        :disabled="currentPage <= 1"
                        aria-label="Previous page"
                      >
                        <i class="fas fa-chevron-left"></i>
                        <span class="ms-1 d-none d-sm-inline">Previous</span>
                      </button>
                    </li>
                    <li
                      v-for="page in visiblePages"
                      :key="page"
                      class="page-item"
                      :class="{ active: page === currentPage }"
                    >
                      <button
                        v-if="typeof page === 'number'"
                        class="page-link"
                        @click="changePage(page)"
                        :aria-label="`Go to page ${page}`"
                        :aria-current="
                          page === currentPage ? 'page' : undefined
                        "
                      >
                        {{ page }}
                      </button>
                      <span v-else class="page-link">{{ page }}</span>
                    </li>
                    <li
                      class="page-item"
                      :class="{ disabled: currentPage >= totalPages }"
                    >
                      <button
                        class="page-link"
                        @click="changePage(currentPage + 1)"
                        :disabled="currentPage >= totalPages"
                        aria-label="Next page"
                      >
                        <span class="me-1 d-none d-sm-inline">Next</span>
                        <i class="fas fa-chevron-right"></i>
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Create/Edit Modal -->
    <PackageCategoryModal
      v-if="showModal"
      :show="showModal"
      :category="selectedCategory"
      :is-edit="isEditMode"
      @close="closeModal"
      @saved="handleCategorySaved"
    />

    <!-- View Modal -->
    <PackageCategoryViewModal
      v-if="showViewModal"
      :show="showViewModal"
      :category="selectedCategory"
      @close="closeViewModal"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, defineAsyncComponent } from "vue";
import { toast } from "vue3-toastify";
import apiService from "@/services/apiService";
import { usePermissions } from "@/composables/usePermissions";

const { canView, canCreate, canUpdate, canDelete } = usePermissions();

const PackageCategoryModal = defineAsyncComponent(
  () => import("@/components/theme/admin/packages/PackageCategoryModal.vue")
);

const PackageCategoryViewModal = defineAsyncComponent(
  () => import("@/components/theme/admin/packages/PackageCategoryViewModal.vue")
);

// State
const categories = ref<any[]>([]);
const loading = ref(false);
const searchQuery = ref("");
const filterStatus = ref("");
const sortBy = ref("sortOrder");
const currentPage = ref(1);
const itemsPerPage = ref(10);
const totalItems = ref(0);

// Modal state
const showModal = ref(false);
const showViewModal = ref(false);
const selectedCategory = ref<any>(null);
const isEditMode = ref(false);

// Computed
const totalPages = computed(() =>
  Math.ceil(totalItems.value / itemsPerPage.value)
);

const startItem = computed(
  () => (currentPage.value - 1) * itemsPerPage.value + 1
);

const endItem = computed(() =>
  Math.min(currentPage.value * itemsPerPage.value, totalItems.value)
);

const visiblePages = computed(() => {
  const delta = 2;
  const range = [];
  const rangeWithDots = [];

  if (totalPages.value <= 1) {
    return [1];
  }

  if (totalPages.value <= 7) {
    // Show all pages if total is 7 or less
    for (let i = 1; i <= totalPages.value; i++) {
      range.push(i);
    }
    return range;
  }

  for (
    let i = Math.max(2, currentPage.value - delta);
    i <= Math.min(totalPages.value - 1, currentPage.value + delta);
    i++
  ) {
    range.push(i);
  }

  if (currentPage.value - delta > 2) {
    rangeWithDots.push(1, "...");
  } else {
    rangeWithDots.push(1);
  }

  rangeWithDots.push(...range);

  if (currentPage.value + delta < totalPages.value - 1) {
    rangeWithDots.push("...", totalPages.value);
  } else if (totalPages.value > 1) {
    rangeWithDots.push(totalPages.value);
  }

  return rangeWithDots.filter(
    (page, index, array) => array.indexOf(page) === index
  );
});

// Methods
const fetchCategories = async () => {
  try {
    loading.value = true;
    const params: any = {
      page: currentPage.value,
      limit: itemsPerPage.value,
    };

    if (searchQuery.value) {
      params.search = searchQuery.value;
    }

    if (filterStatus.value !== "") {
      params.isActive = filterStatus.value;
    }

    if (sortBy.value) {
      params.sortBy = sortBy.value;
    }

    const response = await apiService.getPackageCategories(params);

    // Handle the nested response structure
    if (response.data && response.data.categories) {
      categories.value = response.data.categories;
      totalItems.value =
        response.data.pagination?.totalItems || response.data.categories.length;
    } else if (response.categories) {
      // Fallback if response structure is different
      categories.value = response.categories;
      totalItems.value =
        response.pagination?.totalItems || response.categories.length;
    } else {
      // Fallback for direct array response
      categories.value = response.data || response || [];
      totalItems.value =
        response.total || response.pagination?.total || categories.value.length;
    }
  } catch (error) {
    console.error("Error fetching package categories:", error);
    toast.error("Failed to load package categories");
  } finally {
    loading.value = false;
  }
};

const handleSearch = () => {
  currentPage.value = 1;
  fetchCategories();
};

const handleFilter = () => {
  currentPage.value = 1;
  fetchCategories();
};

const resetFilters = () => {
  searchQuery.value = "";
  filterStatus.value = "";
  sortBy.value = "sortOrder";
  currentPage.value = 1;
  fetchCategories();
};

const changePage = (page: number) => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page;
    fetchCategories();
  }
};

const openCreateModal = () => {
  selectedCategory.value = null;
  isEditMode.value = false;
  showModal.value = true;
};

const editCategory = (category: any) => {
  selectedCategory.value = { ...category };
  isEditMode.value = true;
  showModal.value = true;
};

const viewCategory = (category: any) => {
  selectedCategory.value = category;
  showViewModal.value = true;
};

const closeModal = () => {
  showModal.value = false;
  selectedCategory.value = null;
  isEditMode.value = false;
};

const closeViewModal = () => {
  showViewModal.value = false;
  selectedCategory.value = null;
};

const handleCategorySaved = () => {
  fetchCategories();
  closeModal();
  toast.success(
    isEditMode.value
      ? "Category updated successfully"
      : "Category created successfully"
  );
};

const handleToggleStatus = (id: string, event: Event) => {
  const target = event.target as HTMLInputElement;
  toggleStatus(id, target.checked);
};

const toggleStatus = async (id: string, status: boolean) => {
  try {
    // Optimistic update - update UI immediately
    const categoryIndex = categories.value.findIndex((cat) => cat._id === id);
    if (categoryIndex !== -1) {
      categories.value[categoryIndex].isActive = status;
    }

    await apiService.togglePackageCategoryStatus(id);
    toast.success(
      `Category ${status ? "activated" : "deactivated"} successfully`
    );

    // Refresh data to ensure consistency
    await fetchCategories();
  } catch (error) {
    console.error("Error toggling status:", error);
    toast.error("Failed to update category status");

    // Revert optimistic update on error
    const categoryIndex = categories.value.findIndex((cat) => cat._id === id);
    if (categoryIndex !== -1) {
      categories.value[categoryIndex].isActive = !status;
    }
  }
};

const deleteCategory = async (id: string) => {
  if (
    !confirm(
      "Are you sure you want to delete this category? This action cannot be undone."
    )
  ) {
    return;
  }

  try {
    await apiService.deletePackageCategory(id);
    await fetchCategories();
    toast.success("Category deleted successfully");
  } catch (error) {
    console.error("Error deleting category:", error);
    toast.error("Failed to delete category");
  }
};

const formatDate = (date: string) => {
  if (!date) return "N/A";
  return new Date(date).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

// Lifecycle
onMounted(() => {
  fetchCategories();
});
</script>

<style scoped>
/* Color indicator styling */
.color-indicator {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 2px solid #fff;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.1);
  display: inline-block;
}

/* Enhanced table header styling */
.table thead.table-dark th {
  background-color: var(--bs-dark) !important;
  color: var(--bs-white) !important;
  border-color: var(--bs-dark) !important;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.875rem;
  letter-spacing: 0.025em;
}

.table th,
.table td {
  vertical-align: middle;
  padding: 0.75rem;
}

.table {
  margin-bottom: 0;
}

/* Enhanced pagination styling */
.pagination .page-link {
  color: #495057;
  background-color: #fff;
  border: 1px solid #dee2e6;
  padding: 0.5rem 0.75rem;
  margin-left: -1px;
  line-height: 1.25;
  border-radius: 0;
  transition: color 0.15s ease-in-out, background-color 0.15s ease-in-out,
    border-color 0.15s ease-in-out;
}

.pagination .page-link:hover {
  z-index: 2;
  color: #0056b3;
  background-color: #e9ecef;
  border-color: #dee2e6;
}

.pagination .page-item:first-child .page-link {
  margin-left: 0;
  border-top-left-radius: 0.375rem;
  border-bottom-left-radius: 0.375rem;
}

.pagination .page-item:last-child .page-link {
  border-top-right-radius: 0.375rem;
  border-bottom-right-radius: 0.375rem;
}

.pagination .page-item.active .page-link {
  z-index: 3;
  color: #fff;
  background-color: #0d6efd;
  border-color: #0d6efd;
}

.pagination .page-item.disabled .page-link {
  color: #6c757d;
  background-color: #fff;
  border-color: #dee2e6;
  cursor: not-allowed;
}

/* Table responsiveness improvements */
.table-responsive {
  border-radius: 0.375rem;
  overflow: hidden;
}

.btn-group .btn {
  border-radius: 0.25rem;
  margin-right: 2px;
}

.btn-group .btn:last-child {
  margin-right: 0;
}

code {
  font-size: 0.8rem;
}

.badge {
  font-size: 0.75rem;
}

.text-xs {
  font-size: 0.75rem;
}

/* Card styling improvements */
.card {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  border: 1px solid rgba(0, 0, 0, 0.125);
}

.card-header {
  background-color: rgba(0, 0, 0, 0.03);
  border-bottom: 1px solid rgba(0, 0, 0, 0.125);
}
.table_style table{
  min-width: 1400px;
}
.table_style{
  overflow-x:auto;
}
</style>
